import { addDays } from "date-fns";
import type { Level } from "../types/learning";
import type { OnboardingAnswers, Placement, SkillKey } from "../types/onboarding";
import { goalOptions } from "../data/onboarding";

/** Band boundaries across the 0–1 meter: A1 → A2 → B1. */
const bands: { level: Level; from: number; to: number }[] = [
  { level: "A1", from: 0, to: 0.45 },
  { level: "A2", from: 0.45, to: 0.8 },
  { level: "B1", from: 0.8, to: 1 },
];

const nextOf: Record<string, Level> = { A1: "A2", A2: "B1", B1: "B2", B2: "C1", C1: "C1" };
const HOURS_PER_LEVEL = 45;
const MAX_POINTS = 22;

export function quizFinished(quiz: (boolean | null)[], total: number): boolean {
  const misses = quiz.filter((v) => v !== true).length;
  return misses >= 2 || quiz.length >= total;
}

/**
 * Blends three signals into one estimate:
 * self-report (0–6 pts) + can-do statements (0–8) + quick check (0–8).
 * Without a quick check, can-do answers count double so the scale stays the same.
 */
export function computePlacement(a: OnboardingAnswers): Placement {
  if (a.selfLevel === undefined || a.selfLevel === 0) {
    return { level: "A1", unit: 1, band: 0.03, label: "Complete beginner", checked: false };
  }
  const canDo = a.canDo.reduce<number>((s, v) => s + (v ?? 0), 0);
  const took = a.quiz.length > 0;
  const quizPts = a.quiz.filter((v) => v === true).length * 1.6;
  const points = a.selfLevel * 2 + (took ? canDo + quizPts : canDo * 2);
  const band = Math.min(0.98, Math.max(0.05, points / MAX_POINTS));

  const current = bands.find((b) => band < b.to) ?? bands[bands.length - 1];
  const unit = current.level === "A1" ? 1 + Math.round((band / 0.45) * 3) : 5;
  const label = band < 0.2 ? "Beginner" : band < 0.45 ? "Upper beginner" : band < 0.8 ? "Elementary" : "Pre-intermediate";

  return { level: current.level, unit: Math.min(5, Math.max(1, unit)), band, label, checked: took || a.canDo.some((v) => v !== undefined) };
}

/** Additional languages only get a self-rating, so placement is deliberately conservative. */
export function selfOnlyPlacement(a: OnboardingAnswers, code: string): Placement {
  return computePlacement({ ...a, selfLevel: a.otherLevels[code] ?? 0, canDo: [], quiz: [] });
}

export function projectNextLevel(placement: Placement, minutes: number, today = new Date()) {
  const current = bands.find((b) => b.level === placement.level) ?? bands[0];
  const within = (placement.band - current.from) / (current.to - current.from);
  const remainingHours = HOURS_PER_LEVEL * (1 - Math.min(0.95, Math.max(0, within)));
  const days = Math.ceil((remainingHours * 60) / minutes);
  return { next: nextOf[placement.level], date: addDays(today, days), days };
}

/** Skills that fit the learner's goals, most-requested first (max 3). */
export function suggestedFocus(goals: OnboardingAnswers["goals"]): SkillKey[] {
  const counts = new Map<SkillKey, number>();
  goals.forEach((g) => goalOptions.find((o) => o.id === g)?.boosts.forEach((s) => counts.set(s, (counts.get(s) ?? 0) + 1)));
  const ranked = [...counts.entries()].sort((x, y) => y[1] - x[1]).map(([k]) => k);
  return (ranked.length ? ranked : (["vocab", "speaking"] as SkillKey[])).slice(0, 3);
}
